import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { addAlert } from "service/CSRAdminAlerts";
import { getAdminAlerts } from "redux/slices/getAdminAlertsSlice";
import { useTranslation } from "react-i18next";

import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import StartDate from "./StartDate";
import EndDate from "./EndDate";
import Message from "./Message";
import PortalType from "./PortalType";
import MessageType from "./MessageType";
import Tooltip from "components/General/Tooltip";
import RELEASING_AutoCompleteComboBox from "components/General/AutoCompleteComboBox/RELEASING_AutoCompleteComboBox";

export default function Form() {
  const { t } = useTranslation();
  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
    control,
    setError,
    reset,
  } = useForm();

  const { csrUser } = useSelector((store) => store.csrUser);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [isMessageFill, setIsMessageFill] = useState(false);
  const [selectedOptionPortal, setSelectedOptionPortal] = useState({
    value: alert?.portal ?? "",
  });
  const [selectedOption, setSelectedOption] = useState({
    value: alert?.group ?? "",
  });
  const [selectedOptionTitle, setSelectedOptionTitle] = useState({
    value: alert?.group ?? "",
  });
  const dispatch = useDispatch();

  const submitAlert = async () => {
    const payload = {
      userId: csrUser?.userId,
      id: 0,
      isActive: 1,
      group: getValues("group"),
      type: getValues("type"),
      portal: getValues("portal"),
      userView: getValues("userView"),
      startDate: getValues("startDate"),
      endDate: getValues("endDate"),
      message: getValues("message"),
      title: getValues("title"),
    };
    await addAlert(payload)
      .then((res) => {
        if (res.ok) {
          setConfirmationMessage(t("AgentCSR-Admin-Form-message-1"));
          setConfirmationModal(true);
        } else {
          setConfirmationMessage(t("AgentCSR-Admin-Form-message-2"));
          setConfirmationModal(true);
        }
      })
      .then(() => {
        dispatch(getAdminAlerts());

        reset();
      });
  };

  const isDisabled =
    !getValues("portal") ||
    !getValues("startDate") ||
    !getValues("endDate") ||
    !getValues("title") ||
    !isMessageFill;

  return (
    <>
      <form className="flex flex-col" onSubmit={handleSubmit(submitAlert)}>
        <div className="grid grid-cols-1 gap-y-4 lg:grid-cols-4 lg:gap-x-4 lg:gap-y-8 py-4">
          <PortalType
            errors={errors}
            setValue={setValue}
            control={control}
            setError={setError}
            selectedOption={selectedOptionPortal}
            setSelectedOption={setSelectedOptionPortal}
          />
          <StartDate register={register} setValue={setValue} errors={errors} />

          <EndDate
            register={register}
            setValue={setValue}
            errors={errors}
            startDate={getValues("startDate")}
          />
        </div>
        <div className="grid grid-cols-1 gap-y-4 lg:grid-cols-4 lg:gap-x-4">
          <MessageType
            errors={errors}
            setValue={setValue}
            control={control}
            setError={setError}
            getValues={getValues}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
          />
          <div className="lg:col-span-3">
            <Controller
              name="alertTitle"
              control={control}
              render={() => (
                <RELEASING_AutoCompleteComboBox
                  labelName="Title of Alert"
                  inputPlaceholderText="Select an alert"
                  options={[
                    {
                      value: "",
                      displayAs: "",
                      selectedPolicyDisplayAs: "Select an alert",
                      isDisplayOnly: true,
                    },
                    {
                      value: "importantnotice",
                      displayAs: t("CsrAdmin-important-notice"),
                      selectedPolicyDisplayAs: t("CsrAdmin-important-notice"),
                      isDisplayOnly: true,
                    },
                    {
                      value: "systemmaintenance",
                      displayAs: t("CsrAdmin-system-maintenance"),
                      selectedPolicyDisplayAs: t("CsrAdmin-system-maintenance"),
                      isDisplayOnly: true,
                    },
                  ]}
                  selectedOption={selectedOptionTitle}
                  setSelectedOption={(option) => {
                    if (option?.value) {
                      setValue("title", option?.displayAs);
                      setSelectedOptionTitle(option?.value);
                    }
                  }}
                />
              )}
              rules={{
                required: "This is a required field.",
              }}
            />
          </div>
          <div>
            <div className="flex items-center mb-1">
              <label id={`portalPage-label`}>
                {t("AgentCSR-Admin-ActiveAlert-title-3")}
              </label>
              <Tooltip
                xMargin="mx-0"
                content={t("CsrAdminPortalPage-tooltip-verbiage")}
              />
            </div>
            <input
              id={`userView`}
              disabled={true}
              className="w-full"
              type="text"
              {...register("userView")}
            />
          </div>
          <div className="lg:col-span-3">
            <Message
              register={register}
              errors={errors}
              setError={setError}
              setIsMessageFill={setIsMessageFill}
              MESSAGEMAXIMUMWORDCOUNT={
                getValues("group")?.toLowerCase() === "banner" ? 500 : 300
              }
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            className="ui-btn-primary ui-btn-min-w mt-4"
            disabled={isDisabled}
            onClick={() => submitAlert()}
          >
            {t("AgentCSR-Admin-Form-button")}
          </button>
        </div>
      </form>

      <GeneralPurposeDialog
        state={confirmationModal}
        setState={setConfirmationModal}
        title="CSR Admin Alert"
        desc={confirmationMessage}
      />
    </>
  );
}
